import React, { useContext, useEffect, useState } from 'react';
import { Typography, Box, Button, Dialog, DialogContent, DialogTitle, CircularProgress } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { useAptorApi } from '../../../../../Api';
import { useApiValidationSnackbar } from '../../../../../Api/useValidationSnackbar';
import { SelectOption } from '../../../../../Components/Form/Form.types';
import { AptorApi } from '../../../../../Api/AptorApi';
import Step0SelectView from './Step0SelectView';
import Step1SelectCompanyUnit from './Step1SelectCompanyUnit';
import Step1LawsTableWithFilter from './Step1LawsTableWithFilter';
import Step2SelectType from './Step2SelectType';
import Step2LawActionSelect from './Step2LawActionSelect';
import Step3LawNewCompanyUnitSelect from './Step3LawNewCompanyUnitSelect';
import Step3LawExistingCompanyUnitSelect from './Step3LawExistingCompanyUnitSelect';
import Step3SelectConnection from './Step3SelectConnection';
import Step4LawNewCompanyUnitManagers from './Step4LawNewCompanyUnitManagers';
import Step5RequirementNewCompanyUnitPreview from './Step5RequirementNewCompanyUnitPreview';
import Step5LawConnectionTypeSelect from './Step5LawConnectionTypeSelect';
import Step5LawConnectionActionSelect from './Step5LawConnectionActionSelect';
import Step6EditCompanyUnitUsers from './Step6EditCompanyUnitUsers';
import Step6LawConnectionAddForm from './Step6LawConnectionAddForm';
import Step6LawConnectionDeleteForm from './Step6LawConnectionDeleteForm';
import Step7LawExistingCompanyUnitPreview from './Step7LawExistingCompanyUnitPreview';
import Step5Preview from './Step5Preview';
import DisconnectStep1CompanyUnitsTable from './DisconnectStep1CompanyUnitsTable';
import DisconnectStep2Preview from './DisconnectStep2Preview';
import { EvaluationInfoEnum } from '../../../../../Context/EvaluationInfoEmun';
import Step4RequirementTable from './Step4RequirementTable';
import { IReqConnectionsModel, useRequirementConnectionUpdateState } from '../CustomerRequirement.Update.State';

const PRIMARY = '#c4122f';

const DEFAULT_STEP_INFO = {
  'view-select': {
    updateConnectionInfoSv: 'view-select',
    updateConnectionInfoEn: 'view-select',
  },
  'company-unit-select': {
    updateConnectionInfoSv: 'company-unit-select',
    updateConnectionInfoEn: 'company-unit-select',
  },
  'type-select': {
    updateConnectionInfoSv: 'type-select',
    updateConnectionInfoEn: 'type-select',
  },
  'requirement-select': {
    updateConnectionInfoSv: 'requirement-select',
    updateConnectionInfoEn: 'requirement-select',
  },
  'action-select': {
    updateConnectionInfoSv: 'action-select',
    updateConnectionInfoEn: 'action-select',
  },
  'connection-select': {
    updateConnectionInfoSv: 'connection-select',
    updateConnectionInfoEn: 'connection-select',
  },
  'requirement-connection-type-select': {
    updateConnectionInfoSv: 'requirement-connection-type-select',
    updateConnectionInfoEn: 'requirement-connection-type-select',
  },
  'connection-action-select': {
    updateConnectionInfoSv: 'connection-action-select',
    updateConnectionInfoEn: 'connection-action-select',
  },
  'new-unit-select': {
    updateConnectionInfoSv: 'new-unit-select',
    updateConnectionInfoEn: 'new-unit-select',
  },
  'existing-unit-select': {
    updateConnectionInfoSv: 'existing-unit-select',
    updateConnectionInfoEn: 'existing-unit-select',
  },
  'managers-select': {
    updateConnectionInfoSv: 'managers-select',
    updateConnectionInfoEn: 'managers-select',
  },
  'requirements-table': {
    updateConnectionInfoSv: 'requirements-table',
    updateConnectionInfoEn: 'requirements-table',
  },
  'edit-company-unit-name': {
    updateConnectionInfoSv: 'edit-company-unit-name',
    updateConnectionInfoEn: 'edit-company-unit-name',
  },
  'edit-company-unit-users': {
    updateConnectionInfoSv: 'edit-company-unit-users',
    updateConnectionInfoEn: 'edit-company-unit-users',
  },
  'connection-add': {
    updateConnectionInfoSv: 'connection-add',
    updateConnectionInfoEn: 'connection-add',
  },
  'connection-delete': {
    updateConnectionInfoSv: 'connection-delete',
    updateConnectionInfoEn: 'connection-delete',
  },
  'connection-preview': {
    updateConnectionInfoSv: 'connection-preview',
    updateConnectionInfoEn: 'connection-preview',
  },
  'requirement-existing-unit-preview': {
    updateConnectionInfoSv: 'requirement-existing-unit-preview',
    updateConnectionInfoEn: 'requirement-existing-unit-preview',
  },
  'disconnect-select':{
    updateConnectionInfoSv: 'disconnect-select',
    updateConnectionInfoEn: 'disconnect-select',
  }
};

interface UpdateConnectionWizardDialogProps {
  open: boolean;
  onClose: () => void;
  unitid?: number;
  onComplete?: () => void;
}

export const UpdateConnectionWizardDialog = ({ open, onClose, unitid: initialUnitId, onComplete }: UpdateConnectionWizardDialogProps) => {
  const { api, abortController } = useAptorApi();
  const { companyUnits, getCompanyUnitName } = useContext(UserContext);
  const { formatMessage } = useIntl();
  const { notifySuccess, notifyUnhandledException } = useApiValidationSnackbar();

  const [currentStep, setCurrentStep] = useState<any>(0);
  const [currentView, setCurrentView] = useState<'requirement' | 'connection' | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(initialUnitId || null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState<{ id: number; name: string; number: string } | null>(null);
  const [requirementAction, setRequirementAction] = useState<'new-unit' | 'existing-unit' | 'disconnect-unit' | null>(null);
  const [selectedNewCompanyUnits, setSelectedNewCompanyUnits] = useState<SelectOption[]>([]);
  const [selectedNewManagers, setSelectedNewManagers] = useState<SelectOption[]>([]);
  const [selectedExistingCompanyUnit, setSelectedExistingCompanyUnit] = useState<SelectOption | null>(null);
  const [selectedConnectionType, setSelectedConnectionType] = useState<SelectOption | null>(null);
  const [selectedConnectionAction, setSelectedConnectionAction] = useState<SelectOption | null>(null);
  const [localAddedItems, setLocalAddedItems] = useState<any[]>([]);
  const [localDeletedItems, setLocalDeletedItems] = useState<any[]>([]);
  const [editingSelectedUsers, setEditingSelectedUsers] = useState<SelectOption[]>([]);
  const [updateConnectionInfo, setUpdateConnectionInfo] = useState<any>(null);
  const [selectedDisconnectCompanyUnits, setSelectedDisconnectCompanyUnits] = useState<any[]>([]);

  const companyUnitOptions = companyUnits.map((u) => ({ label: u.name, value: u.id }));
  const selectedUnitOption: SelectOption | undefined = selectedUnitId ? companyUnitOptions.find((u) => u.value === selectedUnitId) : undefined;
  const unit = companyUnits.find((u) => u.id === selectedUnitId);
  const { state, loadRequirementConnections, setType, setConnection, addRequirementConnection, removeRequirementConnection, requirementHasConnection, resetState } = useRequirementConnectionUpdateState(selectedUnitId || 0);


  const fetchUpdateConnectionStepInfo = React.useCallback(() => {
    api
      .getLawportalStepSettings(EvaluationInfoEnum.OtherRequirementupdateConnectionInfo)
      .then((result) => {
        if (abortController.current.signal.aborted) return;
        if (result) {
          try {
            const parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
            setUpdateConnectionInfo(parsedResult);
          } catch (error) {
            console.error('Error parsing evaluation step info:', error);
            setUpdateConnectionInfo(DEFAULT_STEP_INFO);
          }
        } else {
          setUpdateConnectionInfo(DEFAULT_STEP_INFO);
        }
      })
      .catch((err) => {
        console.error('API error fetching step settings:', err);
        setUpdateConnectionInfo(DEFAULT_STEP_INFO);
      });
  }, [api, abortController]);

  useEffect(() => {
    if (open && selectedUnitId) {
      const loadData = async () => {
        try {
          const data = await api.getCustomerCompanyUnitsReqConnections<IReqConnectionsModel>(selectedUnitId);
          if (!abortController.current.signal.aborted) {
            loadRequirementConnections(data);
          }
        } catch (error) {
          if (!abortController.current.signal.aborted) {
            console.error('Failed to load requirement connections:', error);
          }
        }
      };
      loadData();
    }
  }, [api, selectedUnitId, loadRequirementConnections, open, abortController]);

  useEffect(() => {
    if (selectedConnectionType?.value === 'editCompanyUnit' && selectedExistingCompanyUnit && selectedRequirement) {
      const loadUnitData = async () => {
        try {
          const info = await api.getRequirementCompanyUnit<{ id: number; name: string; users: Array<{ id: number; name: string }> }>(
            selectedRequirement.id,
            selectedExistingCompanyUnit.value as number
          );
          if (!abortController.current.signal.aborted) {
            const userOptions = info.users.map((x) => ({
              label: x.name,
              value: x.id,
            }));
            setEditingSelectedUsers(userOptions);
          }
        } catch (error) {
          console.error('Failed to load unit data:', error);
        }
      };
      loadUnitData();
    }
  }, [selectedConnectionType, selectedExistingCompanyUnit, selectedRequirement, api, abortController]);

  useEffect(() => {
    if (open) {
      fetchUpdateConnectionStepInfo();
    }
  }, [open, fetchUpdateConnectionStepInfo]);

  const handleClose = () => {
    setCurrentStep(0);
    setCurrentView(null);
    setSelectedUnitId(initialUnitId || null);
    setIsLoading(false);
    setSelectedRequirement(null);
    setRequirementAction(null);
    setSelectedNewCompanyUnits([]);
    setSelectedNewManagers([]);
    setSelectedExistingCompanyUnit(null);
    setSelectedConnectionType(null);
    setSelectedConnectionAction(null);
    setLocalAddedItems([]);
    setLocalDeletedItems([]);
    setEditingSelectedUsers([]);
    resetState({ reqs: [], aspects: [], processes: [], tags: [], complianceUsers: [] } as IReqConnectionsModel);
    onClose();
  };

  const handleBack = () => {
    if (currentStep === 0) {
      handleClose();
    } else if (
      currentView === 'requirement' &&
      requirementAction === 'existing-unit' &&
      selectedConnectionType?.value === 'editCompanyUnit' &&
      currentStep === 6
    ) {
      setCurrentStep(4);
    } else if (currentView === 'connection' && companyUnits.length === 1 && currentStep === 2) {
      setCurrentStep(0);
    } else if (typeof currentStep === 'number' && currentStep > 0) {
      setCurrentStep((prev) => ((prev as number) - 1) as any);
    }
  };

  const handleUpdateConnectionInfoUpdate = () => {
    fetchUpdateConnectionStepInfo();
  };

  const handleStep0Next = (view: 'requirement' | 'connection') => {
    if (currentView !== view) {
      // Keep selections on Back/Next; clear them only when changing views.
      resetState({ reqs: [], aspects: [], processes: [], tags: [], complianceUsers: [] } as IReqConnectionsModel);
      setSelectedRequirement(null);
      setRequirementAction(null);
      setSelectedNewCompanyUnits([]);
      setSelectedNewManagers([]);
      setSelectedExistingCompanyUnit(null);
      setSelectedConnectionType(null);
      setSelectedConnectionAction(null);
      setLocalAddedItems([]);
      setLocalDeletedItems([]);
      if (view === 'requirement') {
        setSelectedUnitId(null);
        setType({ label: 'Requirement Selection', value: 'process' } as SelectOption);
      }
    }
    setCurrentView(view);
    if (view === 'connection' && companyUnits.length === 1) {
      setSelectedUnitId(companyUnits[0].id);
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
    }
  };

  const handleRequirementCheckboxChange = (requirement: { id: number; name: string; number: string }, isSelected: boolean) => {
    if (isSelected) {
      if (state.connectionAddedToReqs?.length > 0) {
        state.connectionAddedToReqs.forEach((prevRequirement) => {
          removeRequirementConnection(prevRequirement);
        });
      }
      addRequirementConnection(requirement);
      setSelectedRequirement(requirement);
    } else {
      removeRequirementConnection(requirement);
      setSelectedRequirement(null);
    }
  };

  const handleStep1Next = (unit: SelectOption) => {
    setSelectedUnitId(unit.value as number);
    setCurrentStep(2);
  };

  const handleRequirementStep1Next = () => {
    setCurrentStep(2);
  };

  const handleRequirementStep2Next = (action: 'new-unit' | 'existing-unit' | 'disconnect-unit') => {
    // Clear all subsequent data when changing action
    setSelectedNewCompanyUnits([]);
    setSelectedNewManagers([]);
    setSelectedExistingCompanyUnit(null);
    setSelectedConnectionType(null);
    setSelectedConnectionAction(null);
    setLocalAddedItems([]);
    setLocalDeletedItems([]);
    setSelectedDisconnectCompanyUnits([]);
    setRequirementAction(action);
    setCurrentStep(3);
  };

  const handleRequirementNewCompanyUnitsSelect = (units: SelectOption[]) => {
    setSelectedNewCompanyUnits(units);
    setCurrentStep(4);
  };

  const handleRequirementExistingCompanyUnitSelect = (unit: SelectOption) => {
    setSelectedExistingCompanyUnit(unit);
    setCurrentStep(4);
  };

  const handleRequirementDisconnectCompanyUnitsSelect = (units: any[]) => {
    setSelectedDisconnectCompanyUnits(units);
  };

  const handleRequirementExistingCompanyUnitConnectionTypeSelect = (type: SelectOption) => {
    setSelectedConnectionType(type);
    // Clear subsequent data when changing connection type
    setSelectedConnectionAction(null);
    setLocalAddedItems([]);
    setLocalDeletedItems([]);
    setCurrentStep(type.value === 'editCompanyUnit' ? 6 : 5);
  };

  const handleRequirementExistingCompanyUnitConnectionActionSelect = (action: SelectOption) => {
    // Clear local items when switching between Add and Delete
    if (selectedConnectionAction?.value !== action.value) {
      setLocalAddedItems([]);
      setLocalDeletedItems([]);
    }
    setSelectedConnectionAction(action);
    setCurrentStep(6);
  };

  const handleRequirementExistingCompanyUnitSave = async () => {
    if (!selectedRequirement || !selectedExistingCompanyUnit) return;

    setIsLoading(true);
    try {
      const unitId = selectedExistingCompanyUnit.value as number;
      const requirementId = selectedRequirement.id;
      const connectionType = selectedConnectionType?.value;

      // Loop through added items and call API for each
      for (const item of localAddedItems) {
        if (connectionType === 'process') {
          const payload = { processId: item.id, processes: [item.id] };
          await api.addRequirementProcess(requirementId, unitId, payload as any);
        } else if (connectionType === 'aspect') {
          const payload = { aspectId: item.id, aspects: [item.id] };
          await api.addRequirementAspect(requirementId, unitId, payload as any);
        } else if (connectionType === 'tag') {
          const payload = { tagId: item.id, tags: [item.id] };
          await api.addRequirementTag(requirementId, unitId, payload as any);
        }
      }

      // Loop through deleted items and call API for each
      for (const item of localDeletedItems) {
        if (connectionType === 'process') {
          await api.removeRequirementProcess(requirementId, unitId, item.id);
        } else if (connectionType === 'aspect') {
          await api.removeRequirementAspect(requirementId, unitId, item.id);
        } else if (connectionType === 'tag') {
          await api.removeRequirementTag(requirementId, unitId, item.id);
        }
      }
      notifySuccess({ message: formatMessage({ id: 'utils.snackbar.saved' }) });
      if (onComplete) onComplete();
      handleClose();
    } catch (error) {
      setIsLoading(false);
      console.error('Save failed:', error);
    }
  };

  const handleRequirementDisconnectSave = async () => {
    if (!selectedRequirement) return;

    setIsLoading(true);
    try {
      // Loop through selected company units and call disconnect API for each
      for (const unit of selectedDisconnectCompanyUnits) {
        await api.removeRequirementCompanyUnit(selectedRequirement.id, unit.id);
      }
      notifySuccess({ message: formatMessage({ id: 'utils.snackbar.saved' }) });
      if (onComplete) onComplete();
      handleClose();
    } catch (error) {
      setIsLoading(false);
      console.error('Disconnect failed:', error);
    }
  };

  const handleRequirementExistingCompanyUsersSave = async () => {
    if (!selectedRequirement || !selectedExistingCompanyUnit) return;

    setIsLoading(true);
    try {
      const data = {
        complianceUsers: editingSelectedUsers.map((user) => user.value),
      };
      await api.updateRequirementCompanyUnit(selectedRequirement.id, selectedExistingCompanyUnit.value as number, data);
      notifySuccess({ message: formatMessage({ id: 'utils.snackbar.saved' }) });
      if (onComplete) onComplete();
      handleClose();
    } catch (error) {
      setIsLoading(false);
      console.error('Save failed:', error);
    }
  };

  const handleRequirementNewManagersSelect = async (managers: SelectOption[]) => {
    setSelectedNewManagers(managers);
    setCurrentStep(5);
  };

  const saveRequirementNewCompanyUnits = async (startNewConnection: boolean) => {
    if (!selectedRequirement || isLoading) return;

    if (selectedNewCompanyUnits.length === 0) return;

    setIsLoading(true);
    try {
      const formData = {
        units: selectedNewCompanyUnits.map((unit) => unit.value),
        complianceUsers: selectedNewManagers.map((manager) => manager.value),
      };

      await api.addRequirementCompanyUnits(selectedRequirement.id, formData);
      notifySuccess({ message: formatMessage({ id: 'utils.snackbar.saved' }) });
      if (onComplete) onComplete();

      if (startNewConnection) {
        setRequirementAction(null);
        setSelectedNewCompanyUnits([]);
        setSelectedNewManagers([]);
        setCurrentStep(2);
        setIsLoading(false);
      } else {
        handleClose();
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Save failed:', error);
    }
  };

  const handleStep2Next = () => {
    setCurrentStep(3);
  };

  const handleConnectionTypeSelect = (type: SelectOption) => {
    if (state.type?.key !== type.value) {
      setType(type);
    }
  };

  const handleStep3Next = (selectedConnection: SelectOption) => {
    if (state.connection?.id !== selectedConnection.value) {
      setConnection(selectedConnection);
    }
    setCurrentStep(4);
  };

  const handleStep4Next = () => {
    setCurrentStep(5);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      if (state.type && state.connection && selectedUnitId) {
        const action = (i: AptorApi) => i.updateCustomerCompanyUnitsReqConnections(selectedUnitId, state.type!.key, state.connection!.id, state.connectionAddedToReqs.map((x) => x.id), state.connectionRemovedFromReqs.map((x) => x.id));
        const validation = () => {};
        await api.invoke(action, abortController.current, validation, notifyUnhandledException);
        notifySuccess({ message: formatMessage({ id: 'law-portal.manage.update-requirements.update.success' }, { type: state.type ? formatMessage({ id: `law-portal.manage.update-requirements.type.options.${state.type?.key}` }).toLowerCase() : undefined, connection: state.connection?.name }) });
        if (onComplete) onComplete();
        handleClose();
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Save failed:', error);
    }
  };

  const selectedTypeOption: SelectOption | undefined = state.type ? { label: state.type.name, value: state.type.key } : undefined;
  const shouldSkipCompanyUnit = companyUnits.length === 1;
  const isRequirementFlow = currentView === 'requirement';
  const isNewUnitFlow = requirementAction === 'new-unit';
  const isExistingUnitFlow = requirementAction === 'existing-unit';
  const isDisconnectFlow = requirementAction === 'disconnect-unit';
  const isEditCompanyUnitFlow = selectedConnectionType?.value === 'editCompanyUnit';
  const totalSteps = isRequirementFlow && isNewUnitFlow ? 5 : (isRequirementFlow && isExistingUnitFlow ? (isEditCompanyUnitFlow ? 6 : 7) : (isRequirementFlow && isDisconnectFlow ? 4 : (isRequirementFlow ? 7 : (shouldSkipCompanyUnit ? 4 : 5))));
  const displayStep = isRequirementFlow && isExistingUnitFlow && isEditCompanyUnitFlow
    ? (currentStep > 5 ? currentStep - 1 : currentStep as number)
    : (isRequirementFlow ? (currentStep as number) : (shouldSkipCompanyUnit ? (currentStep as number) - 1 : (currentStep as number)));

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <Step0SelectView onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={true} selectedView={currentView as 'requirement' | 'connection' | undefined} onViewSelect={handleStep0Next} currentStepName="view-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
      case 1:
        if (currentView === 'connection') {
          return <Step1SelectCompanyUnit onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} companyUnitOptions={companyUnitOptions} selectedCompanyUnit={selectedUnitOption} onCompanyUnitSelect={handleStep1Next} getCompanyUnitName={getCompanyUnitName} currentStepName="company-unit-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'requirement') {
          return <Step1LawsTableWithFilter onNext={handleRequirementStep1Next} onBack={handleBack} onCancel={handleClose} isFirstStep={false}  connectionAddedToLaws={state.connectionAddedToReqs} lawHasConnection={requirementHasConnection} onLawCheckboxChange={handleRequirementCheckboxChange} currentStepName="requirement-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        }
        return null;
      case 2:
        if (currentView === 'connection') {
          return <Step2SelectType onNext={handleStep2Next} onBack={handleBack} onCancel={handleClose} isFirstStep={false} typeOptions={state.typeOptions} selectedType={selectedTypeOption} onTypeSelect={handleConnectionTypeSelect} currentStepName="type-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'requirement') {
          return <Step2LawActionSelect onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedAction={undefined} onActionSelect={handleRequirementStep2Next} currentStepName="action-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        }
        return null;
      case 3:
        if (currentView === 'requirement' && requirementAction === 'new-unit') {
          return <Step3LawNewCompanyUnitSelect onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} lawId={selectedRequirement?.id} selectedCompanyUnits={selectedNewCompanyUnits} onCompanyUnitsSelect={handleRequirementNewCompanyUnitsSelect} currentStepName="new-unit-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'requirement' && requirementAction === 'existing-unit') {
          return <Step3LawExistingCompanyUnitSelect onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} lawId={selectedRequirement?.id} selectedCompanyUnit={selectedExistingCompanyUnit || undefined} onCompanyUnitSelect={handleRequirementExistingCompanyUnitSelect} currentStepName="existing-unit-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'requirement' && requirementAction === 'disconnect-unit') {
          return <DisconnectStep1CompanyUnitsTable selectedRequirement={selectedRequirement} selectedCompanyUnits={selectedDisconnectCompanyUnits} onCompanyUnitsSelect={handleRequirementDisconnectCompanyUnitsSelect} onNext={() => setCurrentStep(4)} onBack={handleBack} onCancel={handleClose} formatMessage={formatMessage} currentStepName="disconnect-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'connection') {
          return <Step3SelectConnection onNext={() => {}}
          onBack={handleBack}
           onCancel={handleClose}
           isFirstStep={false}
            connectionOptions={state.connectionOptions}
             selectedConnection={state.connection}
             onConnectionSelect={handleStep3Next}
          currentStepName="connection-select"
          updateConnectionInfo={updateConnectionInfo}
          setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate}
           />;
        }
        return null;
      case 4:
        if (currentView === 'requirement' && requirementAction === 'new-unit') {
          return <Step4LawNewCompanyUnitManagers onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedManagers={selectedNewManagers} onManagersSelect={handleRequirementNewManagersSelect} isLoading={isLoading} currentStepName="managers-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'requirement' && requirementAction === 'existing-unit') {
          return <Step5LawConnectionTypeSelect onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedType={selectedConnectionType} onTypeSelect={handleRequirementExistingCompanyUnitConnectionTypeSelect} currentStepName="requirement-connection-type-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'requirement' && requirementAction === 'disconnect-unit') {
          return <DisconnectStep2Preview selectedRequirement={selectedRequirement} selectedCompanyUnits={selectedDisconnectCompanyUnits} onRemove={handleRequirementDisconnectSave} onBack={handleBack} onCancel={handleClose} isLoading={isLoading} />;
        } else if (currentView === 'connection') {
          return <Step4RequirementTable onNext={handleStep4Next} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedCompanyUnit={unit} connectionAddedToReqs={state.connectionAddedToReqs} connectionRemovedFromReqs={state.connectionRemovedFromReqs} requirementHasConnection={requirementHasConnection} addRequirementConnection={addRequirementConnection} removeRequirementConnection={removeRequirementConnection} currentStepName="requirements-table" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        }
        return null;
      case 5:
        if (currentView === 'requirement' && requirementAction === 'new-unit') {
          return <Step5RequirementNewCompanyUnitPreview selectedRequirement={selectedRequirement ?? undefined} selectedCompanyUnits={selectedNewCompanyUnits} selectedManagers={selectedNewManagers} onSaveAndQuit={() => saveRequirementNewCompanyUnits(false)} onSaveAndNewConnection={() => saveRequirementNewCompanyUnits(true)} isSaving={isLoading} />;
        } else if (currentView === 'requirement' && requirementAction === 'existing-unit') {
          return <Step5LawConnectionActionSelect onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedAction={selectedConnectionAction} onActionSelect={handleRequirementExistingCompanyUnitConnectionActionSelect} currentStepName="connection-action-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'connection') {
          return <Step5Preview onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedRowsData={state.connectionAddedToReqs} selectedCompanyUnit={unit} onConfirmUpdate={handleSave} connectionAddedToReqs={state.connectionAddedToReqs} connectionRemovedFromReqs={state.connectionRemovedFromReqs} typeKey={state.type?.key} connectionName={state.connection?.name} currentStepName="connection-preview" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        }
        return null;
      case 6:
        if (currentView === 'requirement' && requirementAction === 'existing-unit') {
          if (selectedConnectionType?.value === 'editCompanyUnit') {
            return <Step6EditCompanyUnitUsers
              onNext={() => setCurrentStep(7)}
              onBack={handleBack}
              onCancel={handleClose}
              isFirstStep={false}
              selectedUsers={editingSelectedUsers}
              onSelectedUsersChange={setEditingSelectedUsers}
              selectedUnitId={selectedExistingCompanyUnit?.value as number}
              currentStepName="edit-company-unit-users" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate}
            />;
          } else if (selectedConnectionAction?.value === 'add') {
            return <Step6LawConnectionAddForm
              onNext={() => setCurrentStep(7)}
              onBack={handleBack}
              onCancel={handleClose}
              isFirstStep={false}
              lawId={selectedRequirement?.id}
              unitId={selectedExistingCompanyUnit?.value as number}
              selectedConnectionType={selectedConnectionType}
              localAddedItems={localAddedItems}
              onAddItem={(item) => setLocalAddedItems([...localAddedItems, item])}
              onRemoveAddedItem={(index) => setLocalAddedItems(localAddedItems.filter((_, i) => i !== index))} currentStepName="connection-add" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate}
            />;
          } else if (selectedConnectionAction?.value === 'delete') {
            return <Step6LawConnectionDeleteForm
              onNext={() => setCurrentStep(7)}
              onBack={handleBack}
              onCancel={handleClose}
              isFirstStep={false}
              lawId={selectedRequirement?.id}
              unitId={selectedExistingCompanyUnit?.value as number}
              selectedConnectionType={selectedConnectionType}
              localDeletedItems={localDeletedItems}
              onDeleteItem={(item) => setLocalDeletedItems([...localDeletedItems, item])}
              onRemoveDeletedItem={(id) => setLocalDeletedItems(localDeletedItems.filter((item) => item.id !== id))} currentStepName="connection-delete" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate}
            />;
          }
        }
        return null;
      case 7:
        if (currentView === 'requirement' && requirementAction === 'existing-unit') {
          if (selectedConnectionType?.value === 'editCompanyUnit') {
            return <Step7LawExistingCompanyUnitPreview
              onNext={() => {}}
              onBack={handleBack}
              onCancel={handleClose}
              isFirstStep={false}
              selectedRequirement={selectedRequirement}
              selectedCompanyUnit={selectedExistingCompanyUnit}
              selectedConnectionType={selectedConnectionType}
              selectedUsers={editingSelectedUsers}
              onSave={handleRequirementExistingCompanyUsersSave}
              isSaving={isLoading}
              currentStepName="requirement-existing-unit-preview"
              updateConnectionInfo={updateConnectionInfo}
              setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate}
            />;
          }
          return <Step7LawExistingCompanyUnitPreview onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedRequirement={selectedRequirement} selectedCompanyUnit={selectedExistingCompanyUnit} selectedConnectionType={selectedConnectionType} selectedAction={selectedConnectionAction} onSave={handleRequirementExistingCompanyUnitSave} isSaving={isLoading} localAddedItems={localAddedItems} localDeletedItems={localDeletedItems} currentStepName="requirement-existing-unit-preview" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} maxWidth="lg" fullWidth disableEscapeKeyDown>
      <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
          {formatMessage({ id: 'law-portal.manage.law-list.update-laws', defaultMessage: 'Update Other Requirements' })}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ paddingTop: '24px', minHeight: '500px', position: 'relative' }}>
        <Box style={{ minHeight: '520px' }}>
          <Box style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Button variant="outlined" onClick={handleClose} style={{ borderColor: PRIMARY, color: PRIMARY, fontWeight: 700 }}>
                {formatMessage({ id: 'form.cancel' })}
              </Button>
            </Box>
            {currentStep > 0 && (
              <Button variant="outlined" onClick={handleBack} style={{ borderColor: PRIMARY, color: PRIMARY, fontWeight: 700 }}>
                {formatMessage({ id: 'component.back-button.back' })}
              </Button>
            )}
          </Box>

          {currentStep > 0 && (
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', marginBottom: '24px', backgroundColor: '#fdf2f3', borderRadius: '6px', borderLeft: `4px solid ${PRIMARY}`, flexWrap: 'wrap', gap: '8px' }}>
              <Typography variant="body2" style={{ fontWeight: 700, color: PRIMARY, fontSize: '13px' }}>
                {formatMessage({ id: 'law-portal.evalution.plan.step' }, { currentStep: displayStep, totalSteps })}
                {currentView === 'requirement' && selectedRequirement?.name && ` — ${selectedRequirement.name}`}
              </Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <Box key={i} style={{ width: '20px', height: '8px', borderRadius: '4px', backgroundColor: i < displayStep ? PRIMARY : '#e0e0e0', transition: 'all 0.3s ease' }} />
                ))}
              </Box>
            </Box>
          )}
          {isLoading && <Box style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 10 }}><CircularProgress /></Box>}
          {renderStep()}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
export default UpdateConnectionWizardDialog;